import { ValidationErrors } from 'final-form';
import React, { ReactNode } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createClientTaskFactory } from '~admin/interactor/client/perform-create-client';
import { ClientProfileData } from '~admin/model/client-profile-data';
import { MediaData } from '~admin/model/media-data';
import { ClientTypeDropdown } from '~admin/view/components/details/client-details/client-dropdowns/client-type-dropdown.component';
import { MediaField } from '~admin/view/components/ui/form/fields/field-media.component';
import { TextField } from '~common/components/form/field-text.component';
import { ClientProfileType } from '~client/model/client-profile-type';
import { MediaType } from '~client/model/media-type';
import { Box } from '~common/components/box.component';
import { FormButton } from '~common/components/button/form-button.component';
import { FormFields } from '~common/components/form/form-fields.component';
import { onSuccessReaction } from '~common/helper/mobx-tasks';
import { useTask } from '~common/hook/use-task';
import { validate } from '~common/validator';
import { required } from '~common/validator/validations';
import { TaskOverlay } from '~web/view/components/layout/task-overlay.component';

interface Props {
  onCancel?: VoidFunction;
  onClientCreated?: (client: ClientProfileData) => void;
}

interface AddClientFormData {
  clientName: string;
  clientType: ClientProfileType;
  defaultMedia: MediaData;
}

export const AddClientOverlay: React.FunctionComponent<Props> = ({ onCancel, onClientCreated }) => {
  const { t } = useTranslation();
  const createClientTask = useTask(createClientTaskFactory);

  onSuccessReaction(createClientTask, (result: ClientProfileData) => {
    onClientCreated?.(result);
  });

  const onSubmit = ({ clientName, clientType, defaultMedia }: AddClientFormData): void => {
    createClientTask({ defaultMediaId: defaultMedia.id, name: clientName, type: clientType });
  };

  const renderForm = (renderProps: FormRenderProps<AddClientFormData>): ReactNode => (
    <FormFields onSubmit={renderProps.handleSubmit} autoComplete={'off'}>
      <Top>
        <MediaField
          name={'defaultMedia'}
          label={t('createEvent.clientDetails.defaultMedia')}
          mediaType={MediaType.IMAGE}
          onMediaSelected={(media: MediaData | null): void => {
            renderProps.form.change('defaultMedia', media);
          }}
        />
      </Top>
      <RestrictBox>
        <TextField name={'clientName'} label={t('createEvent.clientDetails.clientName')} />
      </RestrictBox>
      <RestrictBox>
        <ClientTypeDropdown name={'clientType'} />
      </RestrictBox>
      <ButtonContainer>
        <CreateButton task={createClientTask} isInvalid={renderProps.invalid}>
          {t('createEvent.createButton')}
        </CreateButton>
      </ButtonContainer>
    </FormFields>
  );

  return (
    <TaskOverlay
      title={t('createEvent.clientDetails.createNew')}
      onCancel={onCancel}
      task={createClientTask}
    >
      <Form
        onSubmit={onSubmit}
        render={renderForm}
        validate={(values: AddClientFormData): ValidationErrors | undefined => ({
          clientName: validate(values.clientName, [required()]),
          clientType: validate(values.clientType, [required()]),
          defaultMedia: validate(values.defaultMedia, [required()]),
        })}
      />
    </TaskOverlay>
  );
};

const CreateButton = styled(FormButton)`
  && {
    margin-top: 32px;
  }
`;

const Top = styled(Box)`
  width: 100%;
`;

const ButtonContainer = styled(Box)`
  width: 100%;
  justify-content: flex-end;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin-top: 24px;
`;



// WEBPACK FOOTER //
// ./view/components/details/client-details/add-client-overlay.component.tsx